/**
 * Category chips and in-browser paging, progressive enhancements (hidden without JS):
 *
 * - `<search-cats>` filters the products already on the page by collection or tag (`[data-colls]`): search
 *   results, and the collections with blocks « Catégorie » (sections/main-collection.liquid).
 * - `<grid-pager>` pages such a collection in the browser, since it then holds every product, so that the chip
 *   counts and filter cover the whole collection.
 */

/**
 * Shows the items whose `data-colls` (space separated collection handles) contains `cat`; `all` shows
 * everything. Returns the number of visible items.
 *
 * @param {Iterable<HTMLElement>} items
 * @param {string} cat
 * @returns {number}
 */
export function filterItems(items, cat) {
  let visible = 0;
  for (const item of items) {
    const show = cat === 'all' || (item.dataset.colls ?? '').split(' ').includes(cat);
    item.hidden = !show;
    if (show) visible += 1;
  }
  return visible;
}

export class SearchCats extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    // Inside a collection's results, the chips are re-rendered with every filter or sort change: the category
    // picked before is kept on `<collection-facets>` and applied again, once the grid after them is in place.
    const kept = this.closest('collection-facets')?.getAttribute('data-cat');
    if (kept) queueMicrotask(() => this.select(kept));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cat]') : null;
    if (!(button instanceof HTMLElement)) return;
    // A collection's chip is a link to it: opened in a new tab with Ctrl/⌘/Shift, otherwise it filters here.
    if (button instanceof HTMLAnchorElement) {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
    }
    if (button.getAttribute('aria-disabled') === 'true') return;
    this.select(button.dataset.cat ?? 'all');
    this.closest('collection-facets')?.setAttribute('data-cat', button.dataset.cat ?? 'all');
  };

  /**
   * Space presses a link chip (`role="button"`) like a real button; Enter already clicks it.
   *
   * @param {KeyboardEvent} event
   */
  onKeydown = (event) => {
    if (event.key !== ' ') return;
    const chip = event.target instanceof Element ? event.target.closest('a[data-cat]') : null;
    if (!(chip instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    chip.click();
  };

  /**
   * Presses the chip of `cat` (« all » when it is missing or has no product) and filters the grid.
   *
   * @param {string} cat
   */
  select(cat) {
    const buttons = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-cat]')]);
    const button =
      buttons.find((b) => b.dataset.cat === cat && b.getAttribute('aria-disabled') !== 'true') ??
      buttons.find((b) => b.dataset.cat === 'all');
    if (!button) return;
    for (const other of buttons) other.setAttribute('aria-pressed', String(other === button));
    const target = this.dataset.target ?? '';
    const list = document.getElementById(target);
    if (!list) return;
    list.dataset.cat = button.dataset.cat ?? 'all';
    // A paged grid (the whole collection) shows the first page of the category; otherwise every match.
    const pager = document.querySelector(`grid-pager[data-target="${CSS.escape(target)}"]`);
    if (pager instanceof GridPager) pager.render(1);
    else filterItems(/** @type {NodeListOf<HTMLElement>} */ (list.querySelectorAll('[data-colls]')), list.dataset.cat);
  }
}

/**
 * Page links to show for page `current` of `total`: every page up to 7, else the first, the last and the
 * neighbours of the current page, with `null` for a gap.
 *
 * @param {number} current
 * @param {number} total
 * @returns {(number | null)[]}
 */
export function pagerParts(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  /** @type {(number | null)[]} */
  const parts = [];
  for (let page = 1; page <= total; page += 1) {
    if (page === 1 || page === total || Math.abs(page - current) <= 1) parts.push(page);
    else if (parts.at(-1) !== null) parts.push(null);
  }
  return parts;
}

/**
 * Pages a grid in the browser (`<grid-pager data-target data-size>`): the collection grid holds every product
 * when category chips are offered, so that their counts and filter cover the whole collection. Shows the products
 * of the chosen category (`data-cat` of the grid, set by `<search-cats>`) `data-size` at a time.
 */
export class GridPager extends HTMLElement {
  page = 1;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    queueMicrotask(() => this.render(this.page));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  get list() {
    return document.getElementById(this.dataset.target ?? '');
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-page]') : null;
    if (!(link instanceof HTMLElement)) return;
    event.preventDefault();
    const { page } = link.dataset;
    this.render(page === 'prev' ? this.page - 1 : page === 'next' ? this.page + 1 : Number(page));
    const list = this.list;
    if (list && list.getBoundingClientRect().top < 0) list.scrollIntoView({ block: 'start' });
  };

  /**
   * Shows page `page` of the products matching the grid's category and redraws the links.
   *
   * @param {number} page
   */
  render(page) {
    const list = this.list;
    if (!list) return;
    const size = Math.max(1, Number(this.dataset.size) || 24);
    const cat = list.dataset.cat ?? 'all';
    const items = /** @type {HTMLElement[]} */ ([...list.children]);
    const matches = items.filter((item) => cat === 'all' || (item.dataset.colls ?? '').split(' ').includes(cat));
    const total = Math.max(1, Math.ceil(matches.length / size));
    this.page = Math.min(Math.max(1, page || 1), total);
    const start = (this.page - 1) * size;
    const shown = new Set(matches.slice(start, start + size));
    for (const item of items) item.hidden = !shown.has(item);
    list.dataset.paged = '';

    this.hidden = total < 2;
    const prev = this.querySelector('[data-page="prev"]');
    const next = this.querySelector('[data-page="next"]');
    if (prev instanceof HTMLElement) prev.hidden = this.page === 1;
    if (next instanceof HTMLElement) next.hidden = this.page === total;
    const ul = this.querySelector('ul');
    if (!ul) return;
    const label = this.dataset.pageLabel ?? '0';
    ul.replaceChildren(
      ...pagerParts(this.page, total).map((part) => {
        const li = document.createElement('li');
        const el = document.createElement(part === null || part === this.page ? 'span' : 'a');
        el.className = part === null ? 'gs-pag-gap' : 'gs-pag-n';
        el.textContent = part === null ? '…' : String(part);
        if (part === this.page) el.setAttribute('aria-current', 'page');
        else if (part !== null && el instanceof HTMLAnchorElement) {
          el.href = `#${list.id}`;
          el.dataset.page = String(part);
          el.setAttribute('aria-label', label.replace('0', String(part)));
        }
        li.append(el);
        return li;
      }),
    );
  }
}

if (!customElements.get('search-cats')) customElements.define('search-cats', SearchCats);
if (!customElements.get('grid-pager')) customElements.define('grid-pager', GridPager);
